import React from 'react';
import { Modal, Button, Row, Col, Statistic, Progress, Alert, message } from 'antd';
import {
    FileTextOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    ExclamationCircleOutlined,
    CopyOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
interface MoveResultModalProps {
    visible: boolean;
    results: {
        totalCount: number;
        successCount: number;
        failedCount: number;
        failedTrackingNumbers: string[];
    } | null;
    onClose: () => void;
    onShowNewBags: () => void; // 新增：触发显示新包牌弹窗的回调
}

const MoveToConsolidationResultModal: React.FC<MoveResultModalProps> = ({
    visible,
    results,
    onClose,
    onShowNewBags
}) => {
    const { t } = useTranslation();
    
    if (!results) return null;
    const { totalCount, successCount, failedCount, failedTrackingNumbers } = results;
    const hasFailures = failedCount > 0;

    const handleOk = () => {
        onClose(); // 先关闭当前弹窗
        onShowNewBags(); // 然后触发显示新包牌弹窗
    };

    return (
        <Modal
            visible={visible}
            onCancel={onClose}
            width={600}
            title={t('wms.baggingTask.buttons.moveToConsolidationResult')}
            footer={[
                <Button key="ok" type="primary" onClick={handleOk}>
                    {t('components.button.ok')}
                </Button>
            ]}
        >
            <div className="space-y-4">
                {/* Processing result summary */}
                <div className="bg-gray-50 p-4 rounded-lg">
                    <Row gutter={24}>
                        <Col span={8}>
                            <Statistic
                                title={t('wms.baggingTask.filters.totalProcessed')}
                                value={totalCount}
                                valueStyle={{ color: '#595959' }}
                                prefix={<FileTextOutlined />}
                            />
                        </Col>
                        <Col span={8}>
                            <Statistic
                                title={t('wms.baggingTask.filters.successful')}
                                value={successCount}
                                valueStyle={{ color: '#52c41a' }}
                                prefix={<CheckCircleOutlined />}
                            />
                        </Col>
                        <Col span={8}>
                            <Statistic
                                title={t('wms.baggingTask.filters.failed')}  
                                value={failedCount}
                                valueStyle={{ color: failedCount > 0 ? '#ff4d4f' : '#d9d9d9' }}
                                prefix={<CloseCircleOutlined />}
                            />
                        </Col>
                    </Row>
                </div>

                {/* Failed tracking numbers */}
                {hasFailures && (
                    <div>
                        <div className="mb-3 font-semibold text-red-600 flex items-center">
                            <ExclamationCircleOutlined className="mr-2" />
                            {t('wms.baggingTask.filters.failedTrackingNumbers')} ({failedCount} {t('wms.baggingTask.filters.items')})
                        </div>
                        
                        <div className="max-h-48 overflow-y-auto border rounded-lg p-3 bg-red-50">
                            <div className="grid grid-cols-2 gap-2">
                                {failedTrackingNumbers.map((trackingNumber, index) => (
                                    <div 
                                        key={index} 
                                        className="font-mono text-sm p-2 bg-white rounded border"
                                    >
                                        {trackingNumber}
                                    </div>
                                ))}
                            </div>
                        </div>
                        
                        {/* Copy button only */}
                        <div className="mt-3">
                            <Button 
                                size="small"
                                icon={<CopyOutlined />}
                                onClick={() => {
                                    const failedNumbers = failedTrackingNumbers.join('\n');
                                    navigator.clipboard.writeText(failedNumbers);
                                    message.success(t('wms.baggingTask.messages.copied',{count: failedTrackingNumbers.length}))
                                }}
                            >
                                {t('wms.baggingTask.filters.copyFailedTrk')}
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default MoveToConsolidationResultModal;
